/**
 * Observability Initialization Service
 * Centralizes initialization of analytics, error monitoring, and performance tracking
 * 
 * This should be called once at app startup after auth is initialized
 */

import { initAnalyticsIfNeeded, setAnalyticsUser } from "./analyticsService";
import { initErrorMonitoringIfNeeded, setErrorMonitoringUser } from "./errorMonitoringService";
import { initWebVitals } from "./performanceService";
import { isAnalyticsDebugMode } from "@/config/analyticsConfig";

/**
 * Module-level flag to ensure initialization happens only once
 */
let isObservabilityInitialized = false;

/**
 * Initialize all observability services
 * This should be called once at app startup after Firebase is initialized
 * 
 * @returns Promise that resolves when initialization is complete
 */
export async function initObservability(): Promise<void> {
  // Skip if already initialized
  if (isObservabilityInitialized) {
    if (isAnalyticsDebugMode()) {
      if (import.meta.env.DEV) console.info("[Observability] Already initialized, skipping");
    }
    return;
  }

  if (isAnalyticsDebugMode()) {
    if (import.meta.env.DEV) console.info("[Observability] Starting initialization...");
  }

  try {
    // Initialize services in parallel
    await Promise.all([
      initAnalyticsIfNeeded(),
      initErrorMonitoringIfNeeded(),
      initWebVitals(),
    ]);

    isObservabilityInitialized = true;

    if (isAnalyticsDebugMode()) {
      if (import.meta.env.DEV) console.info("[Observability] ✅ All services initialized");
    }
  } catch (error) {
    console.error("[Observability] Failed to initialize:", error);
    // Mark as initialized even on failure to prevent retry loops
    isObservabilityInitialized = true;
  }
}

/**
 * Set user context across all observability services
 * Call this when user logs in
 * 
 * @param userId - User ID
 * @param userData - Optional user metadata
 */
export function setObservabilityUser(
  userId: string | null,
  userData?: {
    email?: string;
    role?: string;
    isPro?: boolean;
    subscriptionTier?: string;
  }
): void {
  // Set user on analytics
  setAnalyticsUser(userId);

  // Set user on error monitoring
  setErrorMonitoringUser(userId, userData);

  if (isAnalyticsDebugMode()) {
    if (import.meta.env.DEV) console.info("[Observability] User context set:", userId ? "[User ID set]" : "[Cleared]");
  }
}

/**
 * Check if observability is initialized
 */
export function isObservabilityReady(): boolean {
  return isObservabilityInitialized;
}
